import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { APP_LAYOUT_CONTENT_SELECTOR } from '../Scroll';

/** Theater layout space below the frame (layout + content wrapper paddings) */
const BOTTOM_CHROME = 32;

/** Floor so a mis-measurement never collapses the frame */
const MIN_HEIGHT = 320;

/**
 * Measures the exact height (px) left for an embedded viewer frame opened in
 * theater mode: the theater's scrollable area minus whatever sits above the
 * frame (paddings, content name/description rows — which vary per content).
 * A taller frame pushes the embedded viewer's bottom-anchored zoom toolbar
 * out of view and grows a second scrollbar; a fixed shorter one leaves an
 * empty gap when those rows are absent (PCUTS-260).
 *
 * Offsets are measured relative to the theater's scroll container, so the
 * dialog's transform-based opening transition doesn't skew them.
 */
const useTheaterFrameHeight = (active: boolean) => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>();

  const measure = useCallback(() => {
    const node = ref.current;
    const container = node?.closest(APP_LAYOUT_CONTENT_SELECTOR);
    if (!node || !container) {
      return;
    }

    const top =
      node.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop;

    setHeight(
      Math.max(
        Math.round(container.clientHeight - top - BOTTOM_CHROME),
        MIN_HEIGHT
      )
    );
  }, []);

  // Layout effect: measuring after paint would flash the fallback height
  // for a frame before correcting
  useLayoutEffect(() => {
    if (!active) {
      return undefined;
    }

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [active, measure]);

  return { ref, height: active ? height : undefined };
};

export default useTheaterFrameHeight;
